import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FileText, Building2, Calendar,
  MessageSquare, Settings, HelpCircle, Scale, ClipboardCheck,
  FolderOpen, Landmark, ShieldCheck,
} from 'lucide-react';
import { usePersona, Persona } from '@/contexts/PersonaContext';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useUnreadMessageCount } from '@/hooks/useUnreadMessageCount';

interface NavItem {
  title: string;
  href: string;
  icon: React.ElementType;
  roles: Persona[];
}

const dashboardRoutes: Record<Persona, string> = {
  lawyer: '/lawyer-dashboard',
  agent: '/agent-dashboard',
  client: '/client-dashboard',
  superadmin: '/superadmin-dashboard',
};

const navItems: NavItem[] = [
  { title: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Dashboard', href: '/superadmin-dashboard', icon: ShieldCheck, roles: ['superadmin'] },
  { title: 'Transactions', href: '/transactions', icon: FileText, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Properties', href: '/properties', icon: Building2, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Messages', href: '/messages', icon: MessageSquare, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Documents', href: '/documents', icon: FolderOpen, roles: ['lawyer', 'client'] },
  { title: 'Payments', href: '/payments', icon: Landmark, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Calendar', href: '/calendar', icon: Calendar, roles: ['lawyer', 'agent', 'client'] },
  { title: 'Legal Review', href: '/legal-review', icon: Scale, roles: ['lawyer'] },
  { title: 'Due Diligence', href: '/due-diligence', icon: ClipboardCheck, roles: ['lawyer'] },
];

const bottomItems: NavItem[] = [
  { title: 'Settings', href: '/settings', icon: Settings, roles: ['lawyer', 'agent', 'client', 'superadmin'] },
  { title: 'Help Center', href: '/help', icon: HelpCircle, roles: ['lawyer', 'agent', 'client', 'superadmin'] },
];

interface MobileSidebarNavProps {
  onNavigate: () => void;
}

export function MobileSidebarNav({ onNavigate }: MobileSidebarNavProps) {
  const { persona, personaLabel, personaColor } = usePersona();
  const location = useLocation();
  const { data: unreadMessageCount = 0 } = useUnreadMessageCount();

  const filteredNavItems = navItems
    .filter((item) => item.roles.includes(persona))
    .map((item) =>
      item.title === 'Dashboard'
        ? { ...item, href: dashboardRoutes[persona] }
        : item
    );
  const filteredBottomItems = bottomItems.filter((item) => item.roles.includes(persona));

  const renderItem = (item: NavItem) => {
    const isDashboard = item.title === 'Dashboard';
    const isActive = isDashboard
      ? (location.pathname === item.href || (location.pathname === '/' && item.href === dashboardRoutes[persona]))
      : location.pathname === item.href;
    const Icon = item.icon;
    const showMessagesBadge = item.title === 'Messages' && unreadMessageCount > 0;

    return (
      <NavLink
        key={item.href}
        to={item.href}
        onClick={onNavigate}
        className={cn(
          'flex items-center gap-3 px-4 py-3 transition-colors',
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground'
        )}
      >
        <Icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-sidebar-primary')} />
        <span className="text-sm font-medium">{item.title}</span>
        {showMessagesBadge && (
          <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 py-0.5 text-[11px] font-semibold text-destructive-foreground">
            {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
          </span>
        )}
        {isActive && !showMessagesBadge && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-sidebar-primary" />}
      </NavLink>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-65px)] bg-sidebar">
      <div className="px-4 py-3">
        <Badge className={`${personaColor} text-xs`}>{personaLabel}</Badge>
      </div>
      <ScrollArea className="flex-1">
        <nav className="space-y-0.5">
          {filteredNavItems.map(renderItem)}
        </nav>
      </ScrollArea>
      <div className="border-t border-sidebar-border py-2">
        {filteredBottomItems.map(renderItem)}
      </div>
    </div>
  );
}
